import { track } from '@farmersdog/analytics';
import { SubscriptionType, cookie } from '@farmersdog/lead-browser-storage';
import {
  mountTikTokEvent,
  mountISpotRecipesConversion,
  mountAmazonTagAddToShoppingCart,
  ga4Mounter,
  getGa4ItemsFromRecipes,
  TIKTOK_ADDTOCART_EVENT,
  mountSnapchatAddCart,
  generateDeduplicationId,
  mountRedditAddToCart,
  mountAppLovinAddToCart,
} from '@farmersdog/pixels';

import { segmentTrackEvents } from '../analytics';
import { getTosaFacebookPixelAttributes } from '../utils/vendors/facebook';
import { mountXAddToCart } from '../vendors';

import { useRequestAttributesContext } from './useRequestAttributes';

import type { FetchLeadQuery, FetchQuoteQuery } from '../graphql/types';

export function getDiscountedPreTaxTotal(
  quote: FetchQuoteQuery['fetchQuote']
): string {
  return (quote.trial.discount.subtotal / 100).toFixed(2);
}

function getSelectedRecipes(pets: FetchLeadQuery['lead']['pets'] | undefined) {
  if (!pets) {
    return [];
  }

  const allRecipeNames: Set<string> = new Set();

  pets.forEach(pet =>
    pet.selection?.fresh?.options?.recipes?.forEach(recipe =>
      allRecipeNames.add(recipe)
    )
  );

  return Array.from(allRecipeNames);
}

export interface OnRecipesCompletedArgs {
  isLastPet: boolean;
  lead: FetchLeadQuery['lead'] | null;
  quote: FetchQuoteQuery['fetchQuote'] | null;
}

export function useHandleRecipesCompleted() {
  const requestAttributes = useRequestAttributesContext();

  const handleRecipesCompleted = ({
    isLastPet,
    lead,
    quote,
  }: OnRecipesCompletedArgs) => {
    const totalAmount = quote ? getDiscountedPreTaxTotal(quote) : undefined;

    const facebookPixelAttributes = lead
      ? getTosaFacebookPixelAttributes({ lead })
      : undefined;

    const ga4Items = getGa4ItemsFromRecipes(getSelectedRecipes(lead?.pets));
    const eventId = generateDeduplicationId();

    const leadEmail = lead?.email;

    // Run a segment track event for each pet.
    // If you update the payload here, please update the tracking plan in Segment
    // for this event as well:
    // https://app.segment.com/farmersdog/protocols/tracking-plans/tp_1eST2xLpVi5ut60vM5sYE9MsYa1/draft?name=Recipes+-+Success
    track(segmentTrackEvents.recipes_success, {
      totalAmount,
      firstName: lead?.firstName,
      email: leadEmail,
      product_line: SubscriptionType.Fresh,
      label: SubscriptionType.Fresh,
      fbp: cookie.getFacebookPixelCookie(),
      fcp: cookie.getFacebookClickIdCookie(),
      ...requestAttributes,
      ...facebookPixelAttributes,
      eventId: eventId,
      city: lead?.city?.toLowerCase(),
      state: lead?.state?.toLowerCase(),
      zip: lead?.zip,
    });

    ga4Mounter.recipesSuccess({
      data: { items: ga4Items },
      isLastPet,
    });

    // special case - capture each "add to cart" for TikTok
    mountTikTokEvent(TIKTOK_ADDTOCART_EVENT, {
      event_id: eventId,
      email: leadEmail,
    });

    // Only mount pixel events for final pet's recipe page submission.
    if (isLastPet) {
      void mountSnapchatAddCart({ email: leadEmail, eventId });
      mountISpotRecipesConversion({
        utmSource: requestAttributes?.utm_source,
      });
      mountAmazonTagAddToShoppingCart();
      mountXAddToCart();
      mountAppLovinAddToCart({
        value: (quote?.trial.discount.subtotal ?? 0) / 100,
      });
      mountRedditAddToCart({ eventId });
    }
  };

  return handleRecipesCompleted;
}
